import React, { useState, useEffect } from 'react';
import { Check, X, ChevronDown } from 'lucide-react';

interface EditableCellProps {
  value: string | number;
  type?: 'text' | 'number' | 'date' | 'select';
  onSave: (value: string | number) => void;
  className?: string;
  options?: { value: string; label: string }[];
}

const EditableCell: React.FC<EditableCellProps> = ({ value, type = 'text', onSave, className = '', options = [] }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value.toString());

  useEffect(() => {
    setEditValue(value.toString());
  }, [value]);

  const handleSave = () => {
    const finalValue = type === 'number' ? parseFloat(editValue) || 0 : editValue;
    onSave(finalValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value.toString());
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center space-x-1">
        {type === 'select' ? (
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="px-3 py-2 md:px-2 md:py-1 text-base md:text-sm border-2 md:border border-warm-brown rounded-lg md:rounded focus:outline-none focus:ring-2 md:focus:ring-1 focus:ring-accent w-full"
            autoFocus
          >
            <option value="">Seleccionar...</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={type}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="px-3 py-2 md:px-2 md:py-1 text-base md:text-sm border-2 md:border border-warm-brown rounded-lg md:rounded focus:outline-none focus:ring-2 md:focus:ring-1 focus:ring-accent w-full"
            autoFocus
            inputMode={type === 'number' ? 'numeric' : 'text'}
            pattern={type === 'number' ? '[0-9]*' : undefined}
          />
        )}
        <button
          onClick={handleSave}
          className="p-2 md:p-1 text-green-600 hover:text-green-700 touch-manipulation"
          title="Guardar"
        >
          <Check size={20} className="md:w-4 md:h-4" />
        </button>
        <button
          onClick={handleCancel}
          className="p-2 md:p-1 text-red-600 hover:text-red-700 touch-manipulation"
          title="Cancelar"
        >
          <X size={20} className="md:w-4 md:h-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      className={`cursor-pointer hover:bg-cream/50 px-3 py-2 md:px-2 md:py-1 rounded-lg md:rounded flex items-center justify-between touch-manipulation ${className}`}
      onClick={() => setIsEditing(true)}
      title="Click para editar"
    >
      <span>{value || '-'}</span>
      {type === 'select' && <ChevronDown size={16} className="text-medium-gray ml-1 md:w-3.5 md:h-3.5" />}
    </div>
  );
};

export default EditableCell;